import "./chart.scss";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar
} from "recharts";
import { useAuth } from "../../context/AuthContext";
import { formatCurrency } from "../../utils/currencyFormatter";

// Admin data (fallback)
const adminData = [
  { name: "January", Total: 1200 },
  { name: "February", Total: 2100 },
  { name: "March", Total: 800 },
  { name: "April", Total: 1600 },
  { name: "May", Total: 900 },
  { name: "June", Total: 1700 },
];

// Tenant data - payment history (fallback)
const tenantData = [
  { name: "Jan", Payments: 1200 },
  { name: "Feb", Payments: 1200 },
  { name: "Mar", Payments: 1200 },
  { name: "Apr", Payments: 1200 },
  { name: "May", Payments: 1200 },
  { name: "Jun", Payments: 1200 },
];

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

// Custom tooltip formatter
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const dataKey = payload[0].dataKey;
    const value = payload[0].value;
    
    return (
      <div className="custom-tooltip" style={{
        backgroundColor: 'var(--background-primary)',
        border: '1px solid var(--border-color)',
        borderRadius: '8px',
        padding: '10px',
        boxShadow: '0 2px 8px var(--shadow-light)'
      }}>
        <p style={{ margin: '0 0 5px 0', color: 'var(--text-primary)', fontWeight: '600' }}>
          {label}
        </p>
        <p style={{ margin: '0', color: 'var(--text-secondary)' }}>
          {dataKey === 'Total' || dataKey === 'Payments' ? formatCurrency(value) : value}
        </p>
      </div>
    );
  }
  return null;
};

const Chart = ({ aspect, title, data, chartType = "area" }) => {
  const { user } = useAuth();
  
  // Use provided data or fallback to role-based data
  let chartData = data;
  if (!chartData) {
    chartData = user?.role === 'tenant' ? tenantData : adminData;
  }
  
  const chartTitle = user?.role === 'tenant' && !data ? 'Payment History (Last 6 Months)' : title;

  // Determine the data key based on the data structure
  const getDataKey = () => {
    if (!chartData || chartData.length === 0) return 'Total';
    
    const firstItem = chartData[0];
    if (firstItem.Total !== undefined) return 'Total';
    if (firstItem.Payments !== undefined) return 'Payments';
    if (firstItem.value !== undefined) return 'value';
    return 'Total';
  };

  const dataKey = getDataKey();

  const renderAreaChart = () => (
    <AreaChart
      width={730}
      height={250}
      data={chartData}
      margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
    >
      <defs>
        <linearGradient id="total" x1="0" y1="0" x2="0" y2="1">
          <stop offset="5%" stopColor="var(--primary-color)" stopOpacity={0.8} />
          <stop offset="95%" stopColor="var(--primary-color)" stopOpacity={0} />
        </linearGradient>
      </defs>
      <XAxis dataKey="name" stroke="var(--text-secondary)" />
      <YAxis 
        stroke="var(--text-secondary)" 
        tickFormatter={(value) => formatCurrency(value)}
      />
      <CartesianGrid strokeDasharray="3 3" className="chartGrid" />
      <Tooltip content={<CustomTooltip />} />
      <Area
        type="monotone"
        dataKey={dataKey}
        stroke="var(--primary-color)"
        fillOpacity={1}
        fill="url(#total)"
      />
    </AreaChart>
  );

  const renderPieChart = () => (
    <PieChart width={730} height={250}>
      <Pie
        data={chartData}
        cx="50%"
        cy="50%"
        labelLine={false}
        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
        outerRadius={80}
        fill="#8884d8"
        dataKey="value"
      >
        {chartData.map((entry, index) => (
          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
        ))}
      </Pie>
      <Tooltip content={<CustomTooltip />} />
    </PieChart>
  );

  const renderBarChart = () => (
    <BarChart
      width={730}
      height={250}
      data={chartData}
      margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
    >
      <CartesianGrid strokeDasharray="3 3" className="chartGrid" />
      <XAxis dataKey="name" stroke="var(--text-secondary)" />
      <YAxis 
        stroke="var(--text-secondary)" 
        tickFormatter={(value) => formatCurrency(value)}
      />
      <Tooltip content={<CustomTooltip />} />
      <Bar dataKey="value" fill="var(--primary-color)" />
    </BarChart>
  );

  const renderChart = () => {
    switch (chartType) {
      case "pie":
        return renderPieChart();
      case "bar":
        return renderBarChart();
      case "area":
      default:
        return renderAreaChart();
    }
  };

  return (
    <div className="chart">
      <div className="title">{chartTitle}</div>
      <ResponsiveContainer width="100%" aspect={aspect}>
        {renderChart()}
      </ResponsiveContainer>
    </div>
  );
};

export default Chart;
